/**
 * Free Gift Auto-Add
 *
 * When a product with custom.auto_add_products is added to cart,
 * this script automatically adds the referenced gift product(s).
 *
 * Relies on:
 *   - snippets/free-gift-data.liquid outputting .free-gift-data JSON blocks
 *   - FoxTheme.routes and FoxTheme.pubsub from the Hyper theme
 */

(function () {
  'use strict';

  var LOG_PREFIX = '[FreeGift]';

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (r) {
      if (!r.ok) throw new Error(LOG_PREFIX + ' HTTP ' + r.status + ' from ' + url);
      return r.json();
    });
  }

  function withJs(url) {
    return url.indexOf('.js') === -1 ? url + '.js' : url;
  }

  function postJSON(url, body) {
    return fetchJSON(withJs(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });
  }

  function getCart() {
    return fetchJSON(withJs(FoxTheme.routes.cart_url), {
      method: 'GET',
      credentials: 'same-origin',
    });
  }

  // --- Read gift data from Liquid-rendered JSON blocks ---

  function getGiftDataForHandle(parentHandle) {
    var blocks = document.querySelectorAll('script.free-gift-data');
    var gifts = [];
    var seen = {};

    console.log(LOG_PREFIX, 'Found', blocks.length, 'free-gift-data blocks on page');

    blocks.forEach(function (block) {
      try {
        var data = JSON.parse(block.textContent);
        if (data.parentHandle === parentHandle) {
          var key = data.variantId + ':' + data.parentHandle;
          if (!seen[key]) {
            seen[key] = true;
            gifts.push(data);
          } else {
            console.log(LOG_PREFIX, 'Skipping duplicate gift block for variant', data.variantId);
          }
        }
      } catch (e) {
        console.warn(LOG_PREFIX, 'Malformed JSON block', e);
      }
    });

    console.log(LOG_PREFIX, 'Gifts matching handle "' + parentHandle + '":', gifts.length);
    return gifts;
  }

  // --- Check if a gift is already in the cart ---

  function giftAlreadyInCart(cartItems, variantId, parentHandle) {
    return cartItems.some(function (item) {
      return (
        item.variant_id === variantId &&
        item.properties &&
        item.properties._free_gift === 'true' &&
        item.properties._parent_handle === parentHandle
      );
    });
  }

  // --- Add gift to cart ---

  function addGiftToCart(gift) {
    console.log(LOG_PREFIX, 'Adding gift:', gift.title, 'qty:', gift.qty, 'variant:', gift.variantId);
    return postJSON(FoxTheme.routes.cart_add_url, {
      id: gift.variantId,
      quantity: gift.qty,
      properties: {
        _free_gift: 'true',
        _parent_handle: gift.parentHandle,
        _gift_title: gift.title,
      },
    });
  }

  // --- Refresh the cart UI (update body/footer only, preserve header & close button) ---

  function refreshCart() {
    return getCart().then(function (cartJson) {
      console.log(LOG_PREFIX, 'Refreshing cart UI, items:', cartJson.item_count);

      document.querySelectorAll('cart-count').forEach(function (el) {
        var count = cartJson.item_count > 99 ? '99+' : String(cartJson.item_count);
        el.textContent = count;
        if (el.classList.contains('cart-count--absolute')) {
          el.classList.toggle('hidden', cartJson.item_count === 0);
        }
      });

      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        var sectionId = drawer.dataset.sectionId || null;

        if (sectionId) {
          return fetch(FoxTheme.routes.root_url + '?section_id=' + sectionId)
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var parsed = new DOMParser().parseFromString(html, 'text/html');

              var bodyEl = document.getElementById('CartDrawerBody-' + sectionId);
              var footerEl = document.getElementById('CartDrawerFooter-' + sectionId);
              var emptyEl = document.getElementById('CartDrawerEmpty-' + sectionId);

              var newBody = parsed.getElementById('CartDrawerBody-' + sectionId);
              var newFooter = parsed.getElementById('CartDrawerFooter-' + sectionId);
              var newEmpty = parsed.getElementById('CartDrawerEmpty-' + sectionId);

              if (bodyEl && newBody) bodyEl.innerHTML = newBody.innerHTML;
              if (footerEl && newFooter) footerEl.innerHTML = newFooter.innerHTML;
              if (emptyEl && newEmpty) emptyEl.innerHTML = newEmpty.innerHTML;

              if (cartJson.item_count > 0) {
                if (bodyEl) bodyEl.classList.remove('hidden');
                if (footerEl) footerEl.classList.remove('hidden');
                if (emptyEl) emptyEl.classList.add('hidden');
              } else {
                if (bodyEl) bodyEl.classList.add('hidden');
                if (footerEl) footerEl.classList.add('hidden');
                if (emptyEl) emptyEl.classList.remove('hidden');
              }

              console.log(LOG_PREFIX, 'Drawer body/footer updated, header preserved');
            });
        }
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartJson } }));
    });
  }

  // --- Main: listen for product added to cart ---

  var addingGifts = false;

  document.addEventListener('product-ajax:added', function (event) {
    var addedProduct = event.detail && event.detail.product;
    if (!addedProduct) {
      console.log(LOG_PREFIX, 'Event fired but no product in detail');
      return;
    }

    var handle = addedProduct.handle;
    console.log(LOG_PREFIX, 'Product added, handle:', handle);
    if (!handle) return;

    if (addingGifts) {
      console.log(LOG_PREFIX, 'Already processing gifts, skipping duplicate event');
      return;
    }

    var gifts = getGiftDataForHandle(handle);
    if (gifts.length === 0) return;

    addingGifts = true;

    getCart()
      .then(function (cart) {
        var toAdd = gifts;
        console.log(LOG_PREFIX, 'Gifts to add:', toAdd.length);
        if (toAdd.length === 0) return;

        var chain = Promise.resolve();
        toAdd.forEach(function (gift) {
          chain = chain.then(function () {
            return addGiftToCart(gift);
          });
        });

        return chain.then(function () {
          return refreshCart();
        });
      })
      .catch(function (err) {
        console.error(LOG_PREFIX, 'Error adding gift:', err);
      })
      .finally(function () {
        addingGifts = false;
      });
  });

  // --- Orphan removal: remove free gifts whose parent product is no longer in cart ---

  function removeOrphanedGifts() {
    getCart()
      .then(function (cart) {
        var parentHandlesInCart = {};
        cart.items.forEach(function (item) {
          if (!item.properties || item.properties._free_gift !== 'true') {
            parentHandlesInCart[item.handle] = true;
          }
        });

        var orphans = cart.items.filter(function (item) {
          return (
            item.properties &&
            item.properties._free_gift === 'true' &&
            item.properties._parent_handle &&
            !parentHandlesInCart[item.properties._parent_handle]
          );
        });

        if (orphans.length === 0) return;

        console.log(LOG_PREFIX, 'Removing', orphans.length, 'orphaned gift(s)');

        var updates = {};
        orphans.forEach(function (item) {
          updates[item.key] = 0;
        });

        return postJSON(FoxTheme.routes.cart_update_url, { updates: updates }).then(function () {
          return refreshCart();
        });
      })
      .catch(function (err) {
        console.error(LOG_PREFIX, 'Orphan removal error:', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeOrphanedGifts);
  } else {
    removeOrphanedGifts();
  }
})();
